import { useActualIncidentId, useGroupGuid, useUserGuid } from './commonHooks';
import {
  useQuery,
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from 'constants/apiConfig';
import parseErrorMessage from 'utils/parseErrorMessage';
import { applyExerciseIncidentName } from 'utils/incidentExerciseName';
import { noAutoClose } from 'assets/data/config';
import { validate as validateUUID } from 'uuid';
import type { Geometry } from 'geojson';

export type Incident = {
  id: UUID;
  name: string;
  archived: number;
  is_plan?: boolean;
  settings?: Record<string, any>;
  current_period?: UUID;
  escalated_at?: string | null;
  operational_period_interval?: number;
  incident_created?: string;

  active?: boolean;

  severity?: 'Low' | 'Medium' | 'High';
  type?: string; // Medical, Fire, Earthquake, etc
  incident_types: string[]; // [Medical, Fire, Earthquake], etc
  areas_of_responsibility: string[];

  teams_id?: UUID;
  sharepoint_location?: string;
  channel_id?: string;
  tenant_id?: UUID;

  status: 'unstarted' | 'inprogress' | 'done';

  location?: Geometry | null;
};

export const keys = {
  base: 'incident',
  get: (incidentId: UUID) => [keys.base, incidentId],
  all: (group_guid: UUID) => [keys.base, 'all', group_guid],
  periods: (incidentId: UUID) => [keys.base, 'periods', incidentId],
  currentPeriod: (incidentId: UUID) => [keys.base, 'currentPeriod', incidentId],
  futurePeriod: (incidentId: UUID) => [keys.base, 'futurePeriod', incidentId],
  resolveOperationalPeriodFromAndTo: (incidentId: UUID, periodNumber: number) =>
    [
      ...keys.get(incidentId),
      'resolveOperationalPeriodFromAndTo',
      periodNumber,
    ] as const,
};

/**
 * Query options for fetching a single incident - usable with
 * queryClient.fetchQuery()
 */
export const incidentQueryOptions = (incidentId: string) => ({
  queryKey: keys.get(incidentId),
  queryFn: async () => {
    const resp = await axios.get<Incident>(
      `${config.apiGateway.ics_incident}/${incidentId}`
    );
    return resp.data;
  },
});

/**
 * Query options for fetching operational periods - usable with
 * queryClient.fetchQuery()
 */
export const periodsQueryOptions = ({
  incidentId,
  enabled,
}: {
  incidentId: string;
  enabled?: boolean;
}) => ({
  queryKey: keys.periods(incidentId),
  queryFn: async () => {
    const resp = await axios.get<OperationalPeriod[]>(
      `${config.apiGateway.operational_periods}/${incidentId}`
    );
    return resp.data;
  },
  enabled,
});

export const invalidateIncidentQueries = (
  queryClient: QueryClient,
  groupId: string
) => {
  return queryClient.invalidateQueries({
    queryKey: keys.all(groupId),
  });
};

export const useExactIncident = (incidentId: string) => {
  return useQuery({
    ...incidentQueryOptions(incidentId),
    enabled: !!incidentId && validateUUID(incidentId),
  });
};

export const useIncident = () => {
  const incidentId = useActualIncidentId();
  return useExactIncident(incidentId);
};

export const useAllIncidents = () => {
  const group_guid = useGroupGuid();
  return useQuery({
    queryKey: keys.all(group_guid),
    queryFn: async () => {
      const resp = await axios.get<Incident[]>(
        `${config.apiGateway.ics_incidents}/${group_guid}`
      );
      return resp.data;
    },
  });
};

export const useCreateIncident = () => {
  const groupGuid = useGroupGuid();
  const userGuid = useUserGuid();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      incident: Pick<
        Incident,
        'name' | 'areas_of_responsibility' | 'incident_types'
      > & { group_guid: UUID; activated_users: any[] }
    ) => {
      const name = applyExerciseIncidentName(
        incident.name,
        incident.incident_types
      );
      const req = JSON.stringify({
        user_guid: userGuid,
        ...incident,
        name,
      });
      const response = await axios.post<Incident>(
        `${config.apiGateway.createIncident}`,
        req
      );
      return response.data;
    },
    onError: (error: string) => {
      const errorMessage =
        'Error Creating Incident:\n\n' +
        parseErrorMessage(error) +
        '\n\nIf you continue to experience this error, contact support@disastertech.com';
      console.error(errorMessage);
      toast.error(errorMessage, noAutoClose);
    },
    onSuccess: () => {
      invalidateIncidentQueries(queryClient, groupGuid);
    },
  });
};

export type OperationalPeriod = {
  anticipated_end_date_time?: string;
  end_date_time: string;
  id: UUID;
  incident_id: UUID;
  period_number: number;
  start_date_time: string;
  phase_number_completed?: number;
  initial_planned_start?: string | null;
  initial_planned_end?: string | null;
};

export const usePeriods = (enabled?: boolean) => {
  const incidentId = useActualIncidentId();
  const hasValidIncidentId = !!incidentId && validateUUID(incidentId);
  const isEnabled = (enabled ?? true) && hasValidIncidentId;

  return useQuery(
    periodsQueryOptions({
      incidentId,
      enabled: isEnabled,
    })
  );
};

export const useResolveOperationalPeriodFromAndTo = (periodNumber: number) => {
  const incidentId = useActualIncidentId();

  return useQuery({
    queryKey: keys.resolveOperationalPeriodFromAndTo(incidentId, periodNumber),
    queryFn: async () => {
      const { data } = await axios.post<{ opFrom: string; opTo: string }>(
        config.apiGateway.resolveOperationalPeriodFromAndTo,
        { incidentId, periodNumber }
      );
      return data;
    },
    enabled: !!incidentId && validateUUID(incidentId),
  });
};

export const useCurrentOperationalPeriod = () => {
  const incidentId = useActualIncidentId();
  const hasValidIncidentId = !!incidentId && validateUUID(incidentId);

  return useQuery({
    queryKey: keys.currentPeriod(incidentId),
    queryFn: async () => {
      const { data } = await axios.get<OperationalPeriod | null>(
        config.apiGateway.getCurrentPeriod(incidentId)
      );
      return data;
    },
    enabled: hasValidIncidentId,
  });
};

export const useFutureOperationalPeriod = () => {
  const incidentId = useActualIncidentId();
  const hasValidIncidentId = !!incidentId && validateUUID(incidentId);

  return useQuery({
    queryKey: keys.futurePeriod(incidentId),
    queryFn: async () => {
      const { data } = await axios.get<OperationalPeriod | null>(
        config.apiGateway.getFuturePeriod(incidentId)
      );
      return data;
    },
    enabled: hasValidIncidentId,
  });
};

export const useFutureOperationalPeriodNumber = () => {
  const { data: currentPeriod } = useCurrentOperationalPeriod();
  const { data: futurePeriod } = useFutureOperationalPeriod();

  return futurePeriod?.period_number ?? (currentPeriod?.period_number ?? 0) + 1;
};

export const useIncidentPermissions = () => {
  const incidentId = useActualIncidentId();
  const groupGuid = useGroupGuid();

  return useQuery({
    queryKey: ['incidentPermissions', groupGuid, incidentId],
    queryFn: async () => {
      const resp = await axios.post<Record<string, boolean>>(
        config.apiGateway.validateRBACPermissionForAction,
        { group_guid: groupGuid, incident_id: incidentId }
      );
      return resp.data;
    },
    enabled: !!groupGuid && !!incidentId,
  });
};
